<script lang="ts" setup>
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import OrderStopStatus from "@/components/statuses/OrderStopStatus.vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import OrderStopFreightDetails from "@/views/trips/components/OrderStopFreightDetails.vue";
import StopChangesPopover from "@/views/trips/components/StopChangesPopover.vue";
import { useOrder } from "@/composables/useOrder";
import { useFreightOrdinals } from "@/composables/reactive/useFreightOrdinals";
import { type FreightMismatchResult, useFreights } from "@/composables/useFreights";
import type { OrderFreightDto } from "@/api/orders/dto/order.dto";
import type { TripOrder, TripOrderFreight, TripRoute, TripRouteFreight, TripStop } from "@/types/trip";
import { computed } from "vue";
import { OrderFreightState, OrderStopState, OrderStopType } from "@/constants/order";
import { useAppStore } from "@/store/app.store";
import PackageVariantClose from "@/assets/images/package-variant-closed.svg";
import { getEntityKey } from "@/utils/sameEntity";

const { STOP_RELOAD, STOP_DELIVERY, STOP_PICK_UP, timeTypesMap, getStopTime, isReloadStop, orderColorList } =
  useOrder();

const { getFreightName, createOrderFreightFromRouteFreight, getFreightMismatch } = useFreights();

const props = withDefaults(
  defineProps<{
    orders: TripOrder[];
    orderFreights: TripOrderFreight[];
    routes: TripRoute[];
    stop: TripStop;
    tripStops: TripStop[];
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const routeStops = computed(() => {
  return props.routes.flatMap((route) => route.routeStops);
});

const { getFreightOrdinalLabel } = useFreightOrdinals(() => routeStops.value);

const freights = computed<(TripOrderFreight & { routeFreightId?: string })[]>(() => {
  // TODO: set name and number need to be done in TripEdit.vue and there children component should only listen to them(like with order freight number and name)
  if (isReloadStop(props.stop) && props.stop.reloadFreights) {
    return props.stop.reloadFreights
      .map((routeFreight) => {
        const orderFreight = createOrderFreightFromRouteFreight(routeFreight);
        const name = createFreightName(routeFreight, true);
        const number = createFreightNumber(routeFreight);

        return {
          ...orderFreight,
          name,
          number,
          routeFreightId: routeFreight.id,
        };
      })
      .sort((a, b) => a.number - b.number);
  }

  return (props.stop.freights ?? [])
    .map((freightId) => props.orderFreights.find((f) => f.id === freightId))
    .filter((freight): freight is TripOrderFreight => Boolean(freight))
    .sort((a, b) => a.number - b.number);
});

const isPickup = computed(() => {
  return props.stop.type === OrderStopType.pickup;
});

const isDeleted = computed(() => {
  return props.stop.isDeleted;
});

const isUpdated = computed(() => {
  return (
    !isDeleted.value &&
    (props.stop.state === OrderStopState.updated ||
      freights.value.some((f) => f.state !== OrderFreightState.normal || f.isDeleted))
  );
});

const routeFreights = computed<TripRouteFreight[]>(() => {
  return props.routes.flatMap((r) => r.freights);
});

const freightMismatch = computed<FreightMismatchResult | null>(() => {
  if (isReloadStop(props.stop) || !isPickup.value) {
    return null;
  }

  const notReloadRouteFreights = routeFreights.value.filter((f) => !f.parentFreightId);

  return getFreightMismatch(freights.value, notReloadRouteFreights);
});

const reloadFreightsMismatch = computed<FreightMismatchResult | null>(() => {
  if (!isReloadStop(props.stop)) {
    return null;
  }

  const reloadPickUpFreightIds = routeStops.value
    .filter(
      (routeStop) =>
        routeStop.tripStopId === props.stop.id && routeStop.isReload && routeStop.type === OrderStopType.pickup,
    )
    .flatMap((stop) => stop.freights);

  const expectedFreights = freights.value.filter((f) => f.routeFreightId).map((f) => ({ ...f, id: f.routeFreightId! }));

  const reloadRouteFreights = routeFreights.value
    .filter((f) => reloadPickUpFreightIds.includes(getEntityKey(f)) && f.parentFreightId)
    .map((f) => ({ ...f, freightId: f.parentFreightId! }));

  return getFreightMismatch(expectedFreights, reloadRouteFreights);
});

const deletedFreights = computed(() => {
  return freights.value.filter((f) => f.isDeleted);
});

const lastEditBy = computed(() => {
  const appStore = useAppStore();

  if (props.stop.lastEditedBy) {
    return (appStore.getDispatcher(props.stop.lastEditedBy) || {}).name;
  }

  return "";
});

const orderIndex = computed(() => {
  const relatedOrder = props.orders.find((order) => order.id === props.stop.order?.id);
  return props.orders.findIndex((order) => order.id == relatedOrder?.id);
});

const stopClasses = computed(() => {
  if (props.stop.type === STOP_RELOAD) {
    return "reload";
  } else {
    return orderColorList[orderIndex.value];
  }
});

function createFreightName(freight: TripRouteFreight, asPlanned = false): string {
  const label = getFreightOrdinalLabel(getEntityKey(freight));

  return getFreightName(freight, label, "", false, asPlanned);
}

function createFreightNumber(freight: TripRouteFreight): number {
  const label = getFreightOrdinalLabel(getEntityKey(freight));

  return Number(label?.split("-")[1]) || 0;
}

function getFreightClass(freight: OrderFreightDto) {
  return {
    "freight--new": freight.state === OrderFreightState.new,
    "freight--updated": freight.state === OrderFreightState.updated,
    "freight--deleted": freight.isDeleted,
  };
}
</script>

<template>
  <div :class="stopClasses" class="trip-stop__block">

    <div :class="disabled ? 'bg-red-lighten-5' : 'bg-grey-lighten-4'" class="trip-stop__block-inner pa-4 rounded">
      <div class="mb-6 trip-stop__block-header">
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center flex-wrap">
            <v-icon v-if="stop.type === STOP_PICK_UP" :color="isDeleted ? 'grey' : 'green'" class="mr-2" size="20">
              mdi-package-variant-closed
            </v-icon>
            <v-icon v-if="stop.type === STOP_RELOAD" class="mr-2" color="orange" size="20">
              mdi-package-variant-closed
            </v-icon>
            <v-icon
              v-if="stop.type === STOP_DELIVERY"
              :color="isDeleted ? 'grey' : 'red accent-2'"
              class="mr-2"
              size="20"
            >
              mdi-map-marker-radius-outline
            </v-icon>
            <span
              :class="isDeleted ? 'text-grey' : 'text-grey-darken-1'"
              class="font-weight-600 mr-3 font-size-12"
              data-qa="stop-type"
            >
              {{ stop.name }}
            </span>
            <router-link
              v-if="stop.order && !isDeleted"
              :to="{ name: 'order-view', params: { id: stop.order.id } }"
              class="font-size-12 font-weight-medium text-primary text-decoration-none"
              target="_blank"
            >
              Order #{{ stop.order?.number }}
            </router-link>
            <span v-if="isDeleted" class="text-grey font-size-12 font-weight-medium">
              Order #{{ stop.order?.number }}
            </span>
            <stop-changes-popover
              v-if="isUpdated"
              :freights="freights"
              :stop="stop"
              label="Order stop changes"
            ></stop-changes-popover>
          </div>
          <div v-if="isUpdated" class="badge bg-warning pa-2 rounded-te rounded-bs font-weight-600">UPDATED</div>
          <div v-if="isDeleted" class="badge bg-grey-darken-1 pa-2 rounded-te rounded-bs font-weight-600">DELETED</div>
        </div>

        <order-stop-status v-if="!isDeleted" :stop="stop"></order-stop-status>
      </div>

      <div v-if="!isDeleted" class="mb-6 trip-stop__block-body">
        <facility-info
          v-if="stop.facility"
          :facility="stop.facility"
          :updated="isUpdated && !!stop.oldFacilityId && !disabled"
          class="mb-5"
        ></facility-info>
        <div v-if="stop.timeType" class="d-flex align-center text-grey-darken-1">
          <v-icon class="mr-2" size="20">mdi-clock-outline</v-icon>
          <span class="font-weight-medium" data-qa="stop-time">
            {{ timeTypesMap[stop.timeType] }}: {{ getStopTime(stop) }}
          </span>
        </div>
      </div>
      <div v-if="!isDeleted" class="trip-stop__block-footer">
        <div class="d-flex">
          <div class="text-grey-darken-1 mr-2">Freights:</div>
          <div
            v-if="freights.length"
            :class="disabled ? 'text-error' : 'text-grey-darken-2'"
            class="font-weight-medium"
            data-qa="freight-size"
          >
            <div
              v-for="freight in freights"
              :key="freight.id"
              :class="getFreightClass(freight)"
              class="freight d-flex align-center"
            >
              <span>
                {{ freight.name }}
              </span>

              <package-variant-close v-if="!freight.stackable && isPickup" class="ml-2" />
            </div>
          </div>
          <span v-else class="text-grey-darken-1 font-weight-500">No added</span>
        </div>

        <template v-if="freightMismatch">
          <alert-message v-for="freight in freightMismatch.notAllocatedFreights" :key="freight.id" class="mt-3">
            Add Freight {{ freight.name }} to any PU in route(s)
          </alert-message>

          <alert-message
            v-for="mismatchData in freightMismatch.mismatchFreights"
            :key="mismatchData.freightNumber"
            class="mt-3"
            data-qa="updated-freight-data-message"
          >
            Freight {{ mismatchData.freightNumber }} mismatch: {{ mismatchData.diff }}
          </alert-message>
        </template>

        <template v-else-if="reloadFreightsMismatch">
          <alert-message v-if="reloadFreightsMismatch.notAllocatedFreights.length" align="top" class="mt-3">
            Freights:
            <br />
            <div v-for="item of reloadFreightsMismatch.notAllocatedFreights" :key="item.id">
              <span>{{ item.name }}</span>
              <br />
            </div>
            are not selected at any PU reload stops
          </alert-message>

          <alert-message
            v-for="mismatchData in reloadFreightsMismatch.mismatchFreights"
            :key="mismatchData.freightNumber"
            class="mt-3"
            data-qa="updated-freight-data-message"
          >
            Freight {{ mismatchData.freightNumber }} mismatch: {{ mismatchData.diff }}
          </alert-message>
        </template>

        <alert-message
          v-for="deletedFreight in deletedFreights"
          :key="deletedFreight.id"
          bg-color="indigo-lighten-5"
          border-color="none"
          class="mt-3"
          color="indigo"
          icon="mdi-information-outline"
          icon-color="indigo"
        >
          Freight {{ deletedFreight.name }} will be removed after re-dispatch confirmation
        </alert-message>

        <order-stop-freight-details v-if="isPickup && freights.length > 1" :freights="freights" />
      </div>
      <div v-if="isDeleted" class="position-relative h-100" style="margin-top: -40px">
        <div class="absolute-center text-grey font-size-16 text-center">Stop was deleted<br />by {{ lastEditBy }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trip-stop__block-inner {
  height: 100%;
}

.badge {
  margin-top: -16px;
  margin-right: -16px;
}

.freight {
  position: relative;
  width: max-content;

  &:after {
    position: absolute;
    content: "";
    display: none;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    text-decoration: none !important;
    right: -14px;
  }
}

.freight--deleted {
  text-decoration-line: line-through;
  text-decoration-color: $grey-lighter-contrast;
  color: $grey-lighter-contrast !important;

  &:after {
    display: block;
    background-color: $grey-lighter-contrast;
    color: $grey-lighter-contrast;
  }
}

.freight--updated {
  &:after {
    display: block;
    background-color: orange;
  }
}

.freight--new {
  &:after {
    display: block;
    background-color: green;
  }
}
</style>
